import i18n from '@/vue/languages';

const namespace = 'create-new-release-form/';

export const NEW_RELEASE = Object.freeze({
    SET_FORM_FIELD: namespace + 'set_form_field',
    RESET_FORM: namespace + 'reset_form',
    FILES_VALID: namespace + 'validate_files',
    PROPERTIES_VALID: namespace + 'validate_properties',
    FORM_VALID: namespace + 'validate_form',
    FORM: namespace + 'form',
    SUBMIT_FORM: namespace + 'submit_form',
    TO_API_DTO: namespace + 'to_api_dto',
    TO_RELEASE_EDIT: namespace + 'to_release_edit',
    SET_FORM_FROM_API: namespace + 'set_form_from_api',
    SET_FORM_WITH_RELEASE_DETAILS: namespace + 'set_form_with_release_details',
});
export const CREATE_NEW_RELEASE_FORM_FIELDS = {
    files: 'files',
    minVictronConnectVersion: 'minVictronConnectVersion',
    minVersionSettings: 'minVersionSettings',
    minVersion: 'minVersion',
    feedChannels: 'feedChannels',
    changelog: 'changelog',
    title: 'title',
};
export function createForm() {
    const typeArrays = ['files', 'feedChannels'];
    const typeStrings = ['changelog', 'title'];
    return Object.keys(CREATE_NEW_RELEASE_FORM_FIELDS).reduce((acc, key) => {
        if (typeArrays.includes(key)) {
            acc[key] = [];
            return acc;
        }

        if (typeStrings.includes(key)) {
            acc[key] = '';
            return acc;
        }

        acc[key] = null;
        return acc;
    }, {});
}

export function applyData(state, data) {
    const {
        minVictronConnectVersion = null,
        minVersionSettings = null,
        minVersion = null,
        title = '',
    } = data;
    state.minVictronConnectVersion = minVictronConnectVersion;
    state.minVersionSettings = minVersionSettings;
    state.minVersion = minVersion;
    state.title = title;
}

export function applyReleaseDetailData(state, data) {
    const {
        firmwares = [],
        feedChannels = [],
        minVictronConnectVersion = null,
        minVersionSettings = null,
        minVersion = null,
        title = '',
        changelog = '',
    } = data;
    state.minVictronConnectVersion = minVictronConnectVersion;
    state.minVersionSettings = minVersionSettings;
    state.minVersion = minVersion;
    state.title = title;
    state.changelog = changelog;
    state.files = firmwares;
    state.feedChannels = feedChannels;
}

export function resetForm(state, resetFiles) {
    const resetKeys = Object.keys(CREATE_NEW_RELEASE_FORM_FIELDS);
    if (!resetFiles) resetKeys.splice(resetKeys.indexOf('files'), 1);
    resetKeys.forEach(key => {
        const typeArrays = ['files', 'feedChannels'];
        if (typeArrays.includes(key)) {
            state[key] = [];
        }
        state[key] = typeArrays.includes(key) ? [] : null;
    });
}
export default {
    state: createForm,

    getters: {
        [NEW_RELEASE.TO_API_DTO]: state => {
            const {
                files,
                minVictronConnectVersion,
                minVersionSettings,
                minVersion,
                feedChannels,
                changelog,
                title,
            } = state;
            const data = new FormData();
            if (files.every(file => !!file.idLibrary)) {
                files.forEach(function(file) {
                    data.append('idLibraries[]', file.idLibrary);
                });
            } else {
                files.forEach(function(file) {
                    let fileCopy = file;
                    delete fileCopy.idProducts;
                    delete fileCopy.firmwareVersion;
                    delete fileCopy.idLibrary;
                    data.append('firmwareFiles[]', fileCopy);
                });
            }
            data.append('minVictronConnectVersion', minVictronConnectVersion);
            data.append('minVersionSettings', minVersionSettings);
            data.append('minVersion', minVersion);
            data.append('changelog', changelog);
            data.append('title', title);
            feedChannels.forEach(channel => data.append('feedChannels[]', channel));
            return data;
        },
        [NEW_RELEASE.TO_RELEASE_EDIT]: state => (idRelease, originalFiles) => {
            const {
                files,
                minVictronConnectVersion,
                minVersionSettings,
                minVersion,
                feedChannels,
                changelog,
                title,
            } = state;
            const data = {};
            const newIdLibrary = new Set(files.flatMap(file => file.idLibrary));
            const originalIdLibrary = new Set(originalFiles.flatMap(file => file.idLibrary));
            const removed = [...originalIdLibrary].filter(
                idLibrary => !newIdLibrary.has(idLibrary),
            );
            if (removed.length > 0) data.firmwareFilesToDelete = removed;
            data.minVictronConnectVersion = minVictronConnectVersion;
            data.minVersionSettings = minVersionSettings;
            data.minVersion = minVersion;
            data.changelog = changelog;
            data.title = title;
            data.idRelease = idRelease.toString();
            data.feedChannels = feedChannels;
            return data;
        },
        [NEW_RELEASE.FORM]: state => {
            const files = [
                {
                    label: i18n.t('firmware-library.firmwareFiles', {
                        i18nFallback: 'Firmware files(s)',
                    }),
                    value: state.files,
                },
            ].filter(Boolean);

            const properties = [
                {
                    label: i18n.t('firmware-library.minVictronConnectVersion', {
                        i18nFallback: 'Minimum VictronConnect version',
                    }),
                    value: state.minVictronConnectVersion,
                },
                {
                    label: i18n.t('firmware-library.minVersionSettings', {
                        i18nFallback: 'Minimum setting required version',
                    }),
                    value: state.minVersionSettings,
                },
                {
                    label: i18n.t('firmware-library.minVersion', {
                        i18nFallback: 'Minimum firmware version',
                    }),
                    value: state.minVersion,
                },
                {
                    label: i18n.t('firmware-library.selectDistributionChannels', {
                        i18nFallback: 'Select distribution channels',
                    }),
                    value: state.feedChannels,
                },
                {
                    label: i18n.t('firmware-library.changelog', {
                        i18nFallback: 'Changelog',
                    }),
                    value: state.changelog,
                },
                {
                    label: i18n.t('firmware-library.title', {
                        i18nFallback: 'Title',
                    }),
                    value: state.title,
                },
            ].filter(Boolean);

            return { files, properties };
        },
        // FORM VALIDATION
        [NEW_RELEASE.FILES_VALID]: state => {
            return state.files.length > 0;
        },
        // FORM VALIDATION
        [NEW_RELEASE.PROPERTIES_VALID]: state => {
            return (
                state.minVictronConnectVersion !== null &&
                state.minVictronConnectVersion.length > 0 &&
                state.minVersionSettings !== null &&
                state.minVersionSettings.length > 0 &&
                state.minVersion !== null &&
                state.minVersion.length > 0 &&
                state.feedChannels !== null
            );
        },
        [NEW_RELEASE.FORM_VALID]: (state, getters) => {
            const validators = [NEW_RELEASE.FILES_VALID, NEW_RELEASE.PROPERTIES_VALID].filter(
                Boolean,
            );
            return validators.every(validator => getters[validator]);
        },
    },
    mutations: {
        [NEW_RELEASE.SET_FORM_FROM_API]: (state, data) => {
            applyData(state, data);
        },
        [NEW_RELEASE.SET_FORM_WITH_RELEASE_DETAILS]: (state, data) => {
            applyReleaseDetailData(state, data);
        },
        [NEW_RELEASE.RESET_FORM]: (state, resetFiles = true) => {
            resetForm(state, resetFiles);
        },
        [NEW_RELEASE.SET_FORM_FIELD]: (state, { key, value }) => {
            if (!CREATE_NEW_RELEASE_FORM_FIELDS[key]) {
                console.warn(`Unknown form field ${key}`);
                return;
            }
            window.dataLayer.push({
                event: 'gtag.new_release_set_form_field',
                field: key,
                value: value,
            });
            state[key] = value;
        },
    },
};
